// ============================================================
// HARMONIC CHARTER — Chord Formatter
// ============================================================

import type {
  Chord,
  Degree,
  Accidental,
  ChordQuality,
  Extension,
  Key,
  DisplayMode,
  NormalizedAccidental,
} from './types';
import { degreeToNote } from './theory';

// ------------------------------------------------------------
// QUALITY → DISPLAY STRING MAPS
// ------------------------------------------------------------

const QUALITY_NASHVILLE: Record<ChordQuality, string> = {
  maj:              '',
  min:              'm',
  dom7:             '7',
  maj7:             'maj7',
  min7:             'm7',
  dim:              'dim',
  dim7:             'dim7',
  hdim7:            'm7b5',
  aug:              'aug',
  sus2:             'sus2',
  sus4:             'sus4',
  min6:             'm6',
  maj6:             '6',
  '9':              '9',
  maj9:             'maj9',
  min9:             'm9',
  add9:             'add9',
  '11':             '11',
  '13':             '13',
  power:            '5',
  major:            '',
  minor:            'm',
  dominant7:        '7',
  dominantSeventh:  '7',
  major7:           'maj7',
  majorSeventh:     'maj7',
  minor7:           'm7',
  minorSeventh:     'm7',
  diminished:       'dim',
  diminished7:      'dim7',
  halfDiminished:   'm7b5',
  halfDiminished7:  'm7b5',
  halfDiminishedSeventh: 'm7b5',
  augmented:        'aug',
  minorMajor7:      'mMaj7',
  minorMajorSeventh:'mMaj7',
  dominant9:        '9',
  dominantNinth:    '9',
  major9:           'maj9',
  majorNinth:       'maj9',
  minor9:           'm9',
  minorNinth:       'm9',
  suspended2:       'sus2',
  suspended4:       'sus4',
};

const QUALITY_ROMAN: Record<ChordQuality, string> = {
  ...QUALITY_NASHVILLE,
  minor:            '',
  min:              '',
  minorSeventh:     'm7',
  minorNinth:       'm9',
};

const QUALITY_CHORD: Record<ChordQuality, string> = { ...QUALITY_NASHVILLE };

// ------------------------------------------------------------
// EXTENSION → DISPLAY STRING
// ------------------------------------------------------------

const EXTENSION_DISPLAY: Record<Extension, string> = {
  add9:  'add9',
  add11: 'add11',
  add13: 'add13',
  b9:    'b9',
  '#9':  '#9',
  '#11': '#11',
  b13:   'b13',
  '6':   '6',
  '69':  '69',
};

// ------------------------------------------------------------
// ROMAN NUMERAL DEGREE TABLES
// ------------------------------------------------------------

const ROMAN_MAJOR: Record<Degree, string> = {
  1: 'I', 2: 'II', 3: 'III', 4: 'IV',
  5: 'V', 6: 'VI', 7: 'VII',
};

const ROMAN_MINOR: Record<Degree, string> = {
  1: 'i', 2: 'ii', 3: 'iii', 4: 'iv',
  5: 'v', 6: 'vi', 7: 'vii',
};

// ------------------------------------------------------------
// SLASH HELPERS
// ------------------------------------------------------------

interface SlashData {
  degree: number;
  accidental: NormalizedAccidental;
}

function parseSlash(slash: string | null): SlashData | null {
  if (!slash) return null;
  try {
    const parsed = JSON.parse(slash);
    if (typeof parsed === 'object' && parsed !== null && 'degree' in parsed) {
      return parsed as SlashData;
    }
  } catch {
    // legacy plain string fallthrough
  }
  return null;
}

// ------------------------------------------------------------
// SHARED HELPERS
// ------------------------------------------------------------

function formatAccidental(accidental: NormalizedAccidental | Accidental | null): string {
  if (accidental === 'flat'  || accidental === 'b') return 'b';
  if (accidental === 'sharp' || accidental === '#') return '#';
  return '';
}

function formatExtensions(extensions: Extension[]): string {
  return extensions.map((e) => EXTENSION_DISPLAY[e] ?? '').join('');
}

// ------------------------------------------------------------
// NASHVILLE FORMATTER
// FIX: returns '-' for isEmpty chords so they round-trip correctly
// when the bar input string is reconstructed from chord objects.
// ------------------------------------------------------------

export function formatNashville(chord: Chord): string {
  if (chord.isEmpty) return '-';

  const acc     = formatAccidental(chord.accidental);
  const deg     = String(chord.degree);
  const quality = QUALITY_NASHVILLE[chord.quality] ?? '';
  const ext     = formatExtensions(chord.extensions);

  let slash = '';
  const slashData = parseSlash(chord.slash);
  if (slashData) {
    slash = `/${formatAccidental(slashData.accidental)}${slashData.degree}`;
  }

  return `${acc}${deg}${quality}${ext}${slash}`;
}

// ------------------------------------------------------------
// ROMAN NUMERAL FORMATTER
// ------------------------------------------------------------

export function formatRoman(chord: Chord): string {
  if (chord.isEmpty) return '-';

  const acc            = formatAccidental(chord.accidental);
  const isMinorQuality = isMinor(chord.quality);
  const numeral        = isMinorQuality
    ? ROMAN_MINOR[chord.degree]
    : ROMAN_MAJOR[chord.degree];
  const qualitySuffix  = QUALITY_ROMAN[chord.quality] ?? '';
  const ext            = formatExtensions(chord.extensions);

  let slash = '';
  const slashData = parseSlash(chord.slash);
  if (slashData) {
    slash = `/${formatAccidental(slashData.accidental)}${slashData.degree}`;
  }

  return `${acc}${numeral}${qualitySuffix}${ext}${slash}`;
}

// ------------------------------------------------------------
// CHORD NAME FORMATTER
// ------------------------------------------------------------

export function formatChordName(chord: Chord, key: Key): string {
  if (chord.isEmpty) return '-';

  const root    = degreeToNote(chord.degree, chord.accidental, key);
  const quality = QUALITY_CHORD[chord.quality] ?? '';
  const ext     = formatExtensions(chord.extensions);

  let slash = '';
  const slashData = parseSlash(chord.slash);
  if (slashData) {
    const bassNote = degreeToNote(slashData.degree as Degree, slashData.accidental, key);
    if (bassNote) slash = `/${bassNote}`;
  }

  return `${root}${quality}${ext}${slash}`;
}

// ------------------------------------------------------------
// UNIFIED FORMATTER
// ------------------------------------------------------------

export function formatChord(chord: Chord, mode: DisplayMode, key?: Key): string {
  switch (mode) {
    case 'nashville': return formatNashville(chord);
    case 'roman':     return formatRoman(chord);
    case 'chords':
      if (!key) {
        console.warn('formatChord: chord name mode requires a key — falling back to Nashville');
        return formatNashville(chord);
      }
      return formatChordName(chord, key);
  }
}

// ------------------------------------------------------------
// KEY / TIME SIGNATURE LABELS
// ------------------------------------------------------------

export function formatKey(key: Key): string {
  return key.scale === 'minor' ? `${key.root}m` : key.root;
}

export function formatTimeSignature(numerator: number, denominator: number): string {
  return `${numerator}/${denominator}`;
}

// ------------------------------------------------------------
// INTERNAL HELPERS
// ------------------------------------------------------------

function isMinor(quality: ChordQuality): boolean {
  return (
    quality === 'minor' || quality === 'min' ||
    quality === 'minor7' || quality === 'min7' || quality === 'minorSeventh' ||
    quality === 'minor9' || quality === 'min9' || quality === 'minorNinth' ||
    quality === 'halfDiminished' || quality === 'hdim7' ||
    quality === 'halfDiminished7' || quality === 'halfDiminishedSeventh' ||
    quality === 'diminished' || quality === 'dim' ||
    quality === 'diminished7' || quality === 'dim7' ||
    quality === 'minorMajor7' || quality === 'minorMajorSeventh'
  );
}